import React, { useEffect, useMemo, useRef, useState } from "react";
import { ArrowLeftIcon, RotateCwIcon, Trash2Icon, XIcon } from "lucide-react";
import { TransformWrapper, TransformComponent, type ReactZoomPanPinchRef } from "react-zoom-pan-pinch";
import { useAutoAnimate } from "@formkit/auto-animate/react";

import { Button } from "@penmate/react-shared/ui/button";
import { Dialog, DialogClose, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import type { MediaItem } from "@/domain/threadEvent";
import { cn } from "@penmate/react-shared/lib/utils";

const clampIndex = (idx: number, length: number): number => {
  if (length <= 0) return 0;
  if (idx < 0) return 0;
  if (idx >= length) return length - 1;
  return idx;
};

export type MessagePhotosModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  photos: readonly MediaItem[];
  initialIndex: number;
  initialView?: "grid" | "viewer";
  canRemove?: boolean;
  onRemove?: (photo: MediaItem, index: number) => void;
};

export const MessagePhotosModal = ({
  open,
  onOpenChange,
  photos,
  initialIndex,
  initialView,
  canRemove = false,
  onRemove,
}: MessagePhotosModalProps) => {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [confirmRemoveIndex, setConfirmRemoveIndex] = useState<number | null>(null);
  const [rotation, setRotation] = useState(0);
  const [contentAutoAnimateRef] = useAutoAnimate<HTMLDivElement>();
  const [gridAutoAnimateRef] = useAutoAnimate<HTMLDivElement>();
  const [thumbsAutoAnimateRef] = useAutoAnimate<HTMLDivElement>();
  const transformRef = useRef<ReactZoomPanPinchRef | null>(null);
  const resolvedInitialView = useMemo<"grid" | "viewer">(() => {
    if (initialView) return initialView;
    return photos.length > 1 ? "grid" : "viewer";
  }, [initialView, photos.length]);

  const [view, setView] = useState<"grid" | "viewer">(resolvedInitialView);

  useEffect(() => {
    if (!open) return;
    setSelectedIndex(clampIndex(initialIndex, photos.length));
    setView(resolvedInitialView);
    setConfirmRemoveIndex(null);
    setRotation(0);
  }, [open, initialIndex, photos.length, resolvedInitialView]);

  const selected = useMemo(
    () => photos[clampIndex(selectedIndex, photos.length)] ?? null,
    [photos, selectedIndex],
  );

  const selectPhoto = (idx: number) => {
    setSelectedIndex(idx);
    // Reset zoom and rotation when switching photos
    transformRef.current?.resetTransform();
    setRotation(0);
  };

  const rotatePhoto = () => {
    setRotation((prev) => (prev + 90) % 360);
    // Reset zoom when rotating to avoid awkward positioning
    transformRef.current?.resetTransform();
  };

  if (!photos.length) return null;

  const requestRemove = (idx: number) => {
    if (!canRemove || !onRemove) return;
    setConfirmRemoveIndex(clampIndex(idx, photos.length));
  };

  const cancelRemove = () => setConfirmRemoveIndex(null);

  const confirmRemove = () => {
    if (!canRemove || !onRemove) return;
    if (confirmRemoveIndex == null) return;
    const idx = clampIndex(confirmRemoveIndex, photos.length);
    const photo = photos[idx];
    if (!photo) return;
    onRemove(photo, idx);
    setConfirmRemoveIndex(null);
  };

  const CloseButton = () => (
    <DialogClose asChild>
      <button
        type="button"
        className="inline-flex size-9 items-center justify-center rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none"
        aria-label="Close"
      >
        <XIcon className="size-4" />
      </button>
    </DialogClose>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="ph-no-capture pm-photos-modal !flex !h-[90dvh] !flex-col !gap-0 w-[calc(100vw-1.5rem)] max-w-3xl overflow-hidden !p-2 sm:!p-3 [&>button]:hidden">
        <DialogTitle className="sr-only">Photos</DialogTitle>
        <DialogDescription className="sr-only">View photos.</DialogDescription>

        <div ref={contentAutoAnimateRef} className="min-h-0 flex-1">
          {view === "grid" ? (
            <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-auto">
              <div className="flex items-center justify-between gap-2">
                <div className="text-muted-foreground text-sm">
                  {photos.length} {photos.length === 1 ? "photo" : "photos"}
                </div>
                <CloseButton />
              </div>
              <div className="grid grid-cols-3 gap-2 sm:grid-cols-4" ref={gridAutoAnimateRef}>
                {photos.map((p, idx) => (
                  <button
                    key={`${p.url}:${idx}`}
                    type="button"
                    onClick={() => {
                      setSelectedIndex(idx);
                      setView("viewer");
                    }}
                    className={cn(
                      "relative aspect-square w-full overflow-hidden rounded-md border bg-muted",
                      idx === selectedIndex
                        ? "border-ring ring-2 ring-ring/40"
                        : "border-border",
                    )}
                    aria-label={`Open photo ${idx + 1}`}
                    aria-current={idx === selectedIndex ? "true" : undefined}
                  >
                    {canRemove && onRemove ? (
                      <span
                        role="button"
                        tabIndex={0}
                        className="absolute top-1 right-1 z-10 inline-flex size-7 items-center justify-center rounded-full bg-destructive/90 text-destructive-foreground shadow-sm ring-1 ring-destructive/40 hover:bg-destructive focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          requestRemove(idx);
                        }}
                        onKeyDown={(e) => {
                          if (e.key !== "Enter" && e.key !== " ") return;
                          e.preventDefault();
                          e.stopPropagation();
                          requestRemove(idx);
                        }}
                        aria-label={`Remove photo ${idx + 1}`}
                      >
                        <Trash2Icon className="size-4" />
                      </span>
                    ) : null}
                    <img
                      src={p.url}
                      alt={p.filename || `Photo ${idx + 1}`}
                      className="h-full w-full object-cover [image-orientation:from-image]"
                      loading="lazy"
                      decoding="async"
                    />
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <div className="flex min-h-0 flex-1 flex-col gap-2">
              <div className="grid grid-cols-3 items-center gap-2">
                <div className="flex items-center justify-start">
                  {photos.length > 1 ? (
                    <button
                      type="button"
                      className="inline-flex size-9 items-center justify-center rounded-md hover:bg-muted/40 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                      onClick={() => setView("grid")}
                      aria-label="All photos"
                    >
                      <ArrowLeftIcon className="size-5" />
                    </button>
                  ) : (
                    <div className="size-9" />
                  )}
                </div>

                <div className="text-center text-muted-foreground text-xs tabular-nums">
                  {selectedIndex + 1} / {photos.length}
                </div>

                <div className="flex items-center justify-end gap-1">
                  <button
                    type="button"
                    className="inline-flex size-9 items-center justify-center rounded-md hover:bg-muted/40 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                    onClick={rotatePhoto}
                    aria-label="Rotate photo"
                  >
                    <RotateCwIcon className="size-5" />
                  </button>
                  {canRemove && onRemove && selected ? (
                    <button
                      type="button"
                      className="inline-flex size-9 items-center justify-center rounded-md text-destructive hover:bg-destructive/10 hover:text-destructive focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                      onClick={() => requestRemove(selectedIndex)}
                      aria-label="Remove photo"
                    >
                      <Trash2Icon className="size-5" />
                    </button>
                  ) : null}
                  <CloseButton />
                </div>
              </div>

              <div className="relative flex min-h-0 flex-1 w-full items-center justify-center overflow-hidden rounded-md bg-black/10">
                {selected ? (
                  <TransformWrapper
                    ref={transformRef}
                    initialScale={1}
                    minScale={1}
                    maxScale={4}
                    centerOnInit
                    doubleClick={{ mode: "toggle", step: 2 }}
                    panning={{ disabled: false }}
                    pinch={{ disabled: false }}
                    wheel={{ disabled: false, step: 0.1 }}
                  >
                    <TransformComponent
                      wrapperClass="!w-full !h-full"
                      contentClass="!w-full !h-full !flex !items-center !justify-center"
                    >
                      <img
                        src={selected.url}
                        alt={selected.filename || "Photo"}
                        className="max-h-full max-w-full object-contain [image-orientation:from-image] transition-transform duration-200"
                        style={{ transform: `rotate(${rotation}deg)` }}
                        decoding="async"
                      />
                    </TransformComponent>
                  </TransformWrapper>
                ) : null}
              </div>

              <div
                ref={thumbsAutoAnimateRef}
                className={cn(
                  "flex w-full flex-row gap-2 overflow-x-auto pb-1",
                  photos.length <= 1 && "hidden",
                )}
                role="list"
                aria-label="Photo thumbnails"
              >
                {photos.map((p, idx) => (
                  <button
                    key={`${p.url}:${idx}`}
                    type="button"
                    onClick={() => selectPhoto(idx)}
                    className={cn(
                      "h-16 w-16 flex-none overflow-hidden rounded-md border bg-muted",
                      idx === selectedIndex
                        ? "border-ring ring-2 ring-ring/40"
                        : "border-border",
                    )}
                    aria-label={`Photo ${idx + 1}`}
                  >
                    <img
                      src={p.url}
                      alt={p.filename || `Photo ${idx + 1}`}
                      className="h-full w-full object-cover [image-orientation:from-image]"
                      loading="lazy"
                      decoding="async"
                    />
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {canRemove && onRemove && confirmRemoveIndex != null ? (
          <div className="absolute inset-0 z-20 flex items-center justify-center bg-black/50 p-4">
            <div className="w-full max-w-sm rounded-lg bg-background p-4 shadow-lg ring-1 ring-border">
              <div className="font-medium text-sm">Remove photo?</div>
              <div className="mt-3 flex justify-end gap-2">
                <Button type="button" variant="ghost" onClick={cancelRemove}>
                  Cancel
                </Button>
                <Button type="button" variant="destructive" onClick={confirmRemove}>
                  Remove
                </Button>
              </div>
            </div>
          </div>
        ) : null}
      </DialogContent>
    </Dialog>
  );
};
